import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Path, useWatch } from 'react-hook-form';

import { FormSection } from 'common/components/FormSection';
import { SwitchField, TextField } from 'common/forms/Field';
import { TControllerProps } from 'common/types';
import {
    NotificationTargetsSelectField,
    NotificationScaleSelectField,
} from 'certificate/forms/Field';
import { TCertificateFormValues } from 'certificate/types';

type TNotificationSectionFormValues = Pick<
    TCertificateFormValues,
    | 'sendNotification'
    | 'notificationTargets'
    | 'notificationOffset'
    | 'notificationOffsetScale'
> &
    Partial<Pick<TCertificateFormValues, 'sendAchievedMail'>>;

interface INotificationSectionProps<
    TFieldValues extends TNotificationSectionFormValues,
> extends TControllerProps<TFieldValues> {
    hideSendAchievedMail?: boolean;
}

export const NotificationSection = <
    TFieldValues extends TNotificationSectionFormValues,
>({
    control,
    hideSendAchievedMail,
}: INotificationSectionProps<TFieldValues>) => {
    const [translate] = useTranslation();

    const notificationOffset = useWatch({
        control,
        name: 'notificationOffset' as Path<TFieldValues>,
    }) as TNotificationSectionFormValues['notificationOffset'];

    return (
        <FormSection
            renderExpanded
            description={
                <Box>
                    {translate('certificateForm.notificationDescription')}
                </Box>
            }
            title={translate('certificateForm.notificationTitle')}
        >
            {!hideSendAchievedMail && (
                <SwitchField
                    control={control}
                    formControlProps={{ margin: 'none' }}
                    label={translate('certificateForm.sendAchievedMail')}
                    name="sendAchievedMail"
                />
            )}

            <SwitchField
                control={control}
                formControlProps={{ margin: 'none' }}
                label={translate('certificateForm.sendNotification')}
                name="sendNotification"
            />

            <NotificationTargetsSelectField
                control={control}
                name="notificationTargets"
            />

            <Grid container spacing={2}>
                <Grid size={4}>
                    <TextField
                        control={control}
                        label={translate('certificateForm.notificationOffset')}
                        name="notificationOffset"
                    />
                </Grid>

                <Grid size={8}>
                    <NotificationScaleSelectField
                        control={control}
                        name="notificationOffsetScale"
                        notificationOffset={notificationOffset || undefined}
                    />
                </Grid>
            </Grid>
        </FormSection>
    );
};
